import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Inbox, ClipboardList, TrendingUp, AlertCircle } from "lucide-react";
import { getStats, getAlertas, proximas } from "../../lib/adminApi";
import { Card, EstadoBadge, MarcacaoLink, PageTitle, StatCard, formatDate, ClinicaLabel } from "../ui";

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getStats(), getAlertas(), proximas(10)])
      .then(([s, a, u]) => {
        setStats(s); setAlertas(a); setUpcoming(u);
      })
      .finally(() => setLoading(false));
  }, []);

  const delta = stats ? stats.mes.atual - stats.mes.anterior : 0;

  return (
    <div data-testid="admin-dashboard">
      <PageTitle subtitle="Visão geral das marcações e mensagens.">Dashboard</PageTitle>

      {loading ? (
        <p className="text-[#5A5A5A]">A carregar...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              testId="stat-hoje"
              label="Marcações hoje"
              value={stats.hoje.total}
              hint={`Ribeira Brava ${stats.hoje.ribeira} • Calheta ${stats.hoje.calheta}`}
            />
            <Link to="/admin/marcacoes?estado=pendente" className="contents">
              <StatCard
                testId="stat-pendentes"
                label="Pendentes"
                value={stats.pendentes}
                hint="Clique para ver →"
              />
            </Link>
            <StatCard
              testId="stat-proximos7"
              label="Próximos 7 dias"
              value={stats.proximos_7}
              hint="Marcações ativas"
            />
            <StatCard
              testId="stat-mes"
              label="Este mês"
              value={stats.mes.atual}
              hint={`${delta >= 0 ? "+" : ""}${delta} vs. mês anterior (${stats.mes.anterior})`}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card testId="card-proximas" className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-[#1E5F6E]" />
                  <h2 className="font-display text-lg font-bold">Próximas marcações</h2>
                </div>
                <Link to="/admin/marcacoes" className="text-xs font-bold text-[#1E5F6E] hover:underline">
                  Ver todas →
                </Link>
              </div>
              {upcoming.length === 0 ? (
                <p className="text-sm text-[#5A5A5A]">Nenhuma marcação próxima.</p>
              ) : (
                <ul className="divide-y divide-[#1E5F6E]/10">
                  {upcoming.map((m) => (
                    <li key={m.id} className="py-3 flex items-center justify-between gap-3" data-testid={`upcoming-${m.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#1A1A1A]">
                          {formatDate(m.data_hora)} • <ClinicaLabel slug={m.clinica_slug} />
                        </div>
                        <div className="text-sm text-[#5A5A5A] truncate">
                          <MarcacaoLink id={m.id}>{m.numero_marcacao}</MarcacaoLink> · {m.tutor.nome} — {m.animal.nome}
                        </div>
                      </div>
                      <EstadoBadge estado={m.estado} />
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <div className="space-y-5">
              <Card testId="card-mensagens">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Inbox className="h-4 w-4 text-[#1E5F6E]" />
                    <h2 className="font-display text-lg font-bold">Mensagens novas</h2>
                  </div>
                  <Link to="/admin/mensagens" className="text-xs font-bold text-[#1E5F6E] hover:underline">
                    Ver →
                  </Link>
                </div>
                <div className="font-display text-3xl font-extrabold">{stats.mensagens_novas}</div>
                <div className="text-xs text-[#5A5A5A] mt-1">por ler</div>
              </Card>

              <Card testId="card-alertas">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-[#E8A87C]" />
                  <h2 className="font-display text-lg font-bold">Alertas</h2>
                </div>
                {alertas?.amanha_pendentes > 0 ? (
                  <div className="rounded-xl bg-[#E8A87C]/10 border border-[#E8A87C]/40 px-4 py-3 text-sm">
                    <strong>{alertas.amanha_pendentes}</strong> marcação(ões) de amanhã ainda{" "}
                    <strong>pendente(s)</strong> de confirmação.
                  </div>
                ) : (
                  <p className="text-sm text-[#5A5A5A]">Sem alertas.</p>
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
